import { SearchLg } from '@untitled-ui/icons-react/build/cjs';
import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import CommonModal from 'src/components/CommonModal';
import InputText from 'src/components/InputText/InputText';
import NotFoundUI from 'src/components/NotFoundUI';
import Radio from 'src/components/Radio/Radio';
import { PATH } from 'src/constants/path';
import { RootState } from 'src/redux/store';
import { getShortName } from 'src/utils/CommonFunctions';

import { setCreateQuoteData, divisionId } from '../CreateQuote/CreateQuote.slice';

export const ORDER_PARAM: any = {
    search: '',
    limit: 5,
};

interface SelectDivisionProps {
    handleClose: () => void;
    onDivisionSelected?: (divisionId: number) => void;
}

const SelectDivision = ({ handleClose, onDivisionSelected }: SelectDivisionProps) => {
    const { t } = useTranslation();
    const currentUser = useSelector((state: RootState) => state.auth.currentUser);
    const currentDivisionId = useSelector(divisionId);
    const divisions = currentUser?.divisions || [];
    const navigate = useNavigate();
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedDivision, setSelectedDivision] = useState<any>(divisions.find((div: any) => div.id === currentDivisionId) || null);
    const dispatch = useDispatch();

    // Filter divisions based on search query
    const filteredDivisions = useMemo(() => {
        if (!searchQuery.trim()) {
            return divisions;
        }
        const lowerQuery = searchQuery.toLowerCase();

        return divisions.filter(
            (division: any) => division?.displayName?.toLowerCase().includes(lowerQuery) || division?.city?.toLowerCase().includes(lowerQuery) || division?.postal?.toLowerCase().includes(lowerQuery)
        );
    }, [divisions, searchQuery]);

    const handleSaveDivision = () => {
        if (selectedDivision) {
            dispatch(setCreateQuoteData({ data: { divisionId: selectedDivision?.id } }));
            handleClose();

            // Call the callback to trigger billing account modal or navigate
            if (onDivisionSelected) {
                onDivisionSelected(selectedDivision?.id);
            } else {
                // Fallback to direct navigation if no callback provided
                navigate(PATH.CREATE_QUOTE);
            }
        }
    };

    return (
        <CommonModal
            title={t('MyOrders.Select_Division')}
            handleClose={handleClose}
            size={'max-w-[540px] h-[576px]'}
            primaryBtnText={'Select Division'}
            primaryBtnOnClick={handleSaveDivision}
            primaryBtnLoading={false}
            primaryBtnDisabled={!selectedDivision}
            secondaryBtnText={'Cancel'}
            secondaryBtnOnClick={() => handleClose()}
            secondaryBtnDisabled={false}
        >
            <div className="pt-5 pb-2.5 [&>*]:px-5 space-y-5 h-full flex flex-col">
                <InputText
                    label={t('MyOrders.Select_Division_Label')}
                    labelClassName="block mb-0"
                    inputName="searchQuote"
                    placeholder={t('MyOrders.Select_Division_Placeholder')}
                    className="bg-white focus:bg-white pl-8 pr-7 placeholder:text-gray500 shadow-sm font-normal search-input form_control"
                    icon={<SearchLg className="absolute top-1/2 -translate-y-1/2 left-2 text-grayText h-4 w-4" />}
                    labelWrapClass="mb-1.5"
                    value={searchQuery}
                    inputType="text"
                    onChangeFunc={(e) => {
                        setSearchQuery(e?.target?.value || '');
                    }}
                    isClearable={true}
                />

                {filteredDivisions.length > 0 ? (
                    <ul className="flex flex-col space-y-2 flex-1 overflow-auto custom-scrollbar-v2 pb-2.5">
                        {filteredDivisions.map((item: any, index: number) => {
                            const isSelected = selectedDivision?.id === item?.id;

                            return (
                                <li
                                    className={`rounded-lg hover:bg-utilityGray100 p-3 flex gap-x-3 items-center cursor-pointer ${isSelected ? 'bg-utilityGray100' : ''}`}
                                    key={index}
                                    onClick={() => {
                                        setSelectedDivision(item);
                                    }}
                                >
                                    <div className="w-8 h-8 rounded-full border border-utilityBlack border-opacity-[0.08] flex-none flex items-center justify-center uppercase bg-utilityGray100 text-primary text-sm">
                                        {getShortName(item.displayName)}
                                    </div>
                                    <div className="flex-1 truncate">
                                        <h6 className="text-grayLight900 text-sm font-medium flex-1">{item.displayName}</h6>
                                        <p className="text-grayLight600 text-xs font-normal">
                                            {item.city}
                                            {item.postal ? `, ${item.postal}` : ''}
                                        </p>
                                    </div>
                                    <Radio
                                        id={item.id}
                                        onChangeFunc={() => {
                                            setSelectedDivision(item);
                                        }}
                                        checked={selectedDivision?.id === item?.id}
                                    />
                                </li>
                            );
                        })}
                    </ul>
                ) : (
                    <NotFoundUI title={t('MyOrders.No_division_found')} desc={t('MyOrders.No_division_found_description')} containerClassName="!h-auto flex-1 !my-6" />
                )}
            </div>
        </CommonModal>
    );
};

export default SelectDivision;
